import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import type { SiteMapPoint, SiteGeocodingIssues } from '@/routes/map/SitesMapPage.types'
import { toMapSite } from '@/routes/map/SitesMapPage.utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

const fetchSiteMapPoints = async (signal: AbortSignal) => {
  const response = await useQsAdminApiManager.getState().client.get<SiteMapPoint[]>('/api/sedi/mappa-punti', { signal })
  return response.data.map(toMapSite)
}

/** Load once; location, viewport and common filters stay in the browser. */
export const useSiteMapPoints = () =>
  useQuery({
    queryKey: ['siteMapPoints'],
    queryFn: ({ signal }) => fetchSiteMapPoints(signal),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  })

export const useSiteGeocodingIssues = (enabled: boolean) =>
  useQuery({
    queryKey: ['siteGeocodingIssues'],
    queryFn: async ({ signal }) => {
      const response = await useQsAdminApiManager
        .getState()
        .client.get<SiteGeocodingIssues>('/api/sedi/geocoding-issues', { signal })
      return response.data
    },
    enabled,
    refetchOnWindowFocus: false,
  })

export const useRequeueSiteGeocoding = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (siteId: number) =>
      useQsAdminApiManager.getState().client.post(`/api/sedi/${siteId}/geocoding/requeue`),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['siteGeocodingIssues'] }),
        queryClient.invalidateQueries({ queryKey: ['siteMapPoints'] }),
      ]),
  })
}

export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(handler)
  }, [value, delay])
  return debouncedValue
}
